import { useEffect, useState } from "react";
import { X, Loader2, Shield, Users, Save } from "lucide-react";
import { toast } from "react-hot-toast";
import ssoProvidersService, {
  SSOProvider,
} from "../../services/sso-providers.service";
import permissionProvidersService, {
  PermissionProvider,
  UpdatePermissionProviderData,
} from "../../services/permission-providers.service";

interface LoginSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

export default function LoginSettingsModal({
  isOpen,
  onClose,
  onSave,
}: LoginSettingsModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [classicalProvider, setClassicalProvider] = useState(true);
  const [ssoProviders, setSsoProviders] = useState<SSOProvider[]>([]);
  const [originalPermissionData, setOriginalPermissionData] =
    useState<PermissionProvider | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Fetch both permission providers and SSO providers
      const [permissionResponse, ssoResponse] = await Promise.all([
        permissionProvidersService.getPermissionProviders(),
        ssoProvidersService.getProviders({ skip: 0, limit: 1000 }), // Get all SSO providers
      ]);

      // Set permission data
      if (permissionResponse.data && permissionResponse.data.length > 0) {
        const permissionData = permissionResponse.data[0];
        setOriginalPermissionData(permissionData);
        setClassicalProvider(permissionData.classical_provider);
      } else {
        setClassicalProvider(true);
      }

      // Set SSO providers data
      if (ssoResponse.data && ssoResponse.data.login_provider_list) {
        setSsoProviders(ssoResponse.data.login_provider_list);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
      toast.error("Failed to load login settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSsoProviderToggle = (providerId: string) => {
    setSsoProviders((prev) =>
      prev.map((provider) =>
        provider._id === providerId
          ? { ...provider, enable: !provider.enable }
          : provider
      )
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Update permission providers (classical provider setting)
      const socialProviderData = ssoProviders.map((provider) => ({
        provider_name: provider.provider_name,
        is_active: provider.enable,
      }));

      const updatePermissionData: UpdatePermissionProviderData = {
        classical_provider: classicalProvider,
        social_provider: socialProviderData,
      };

      // Update individual SSO provider enable status
      const ssoUpdatePromises = ssoProviders.map((provider) =>
        ssoProvidersService.toggleProviderStatus(provider._id, provider.enable)
      );

      // Execute both permission update and SSO updates
      await Promise.all([
        permissionProvidersService.updatePermissionProviders(
          updatePermissionData
        ),
        ...ssoUpdatePromises,
      ]);

      toast.success("Login settings updated successfully!");
      onSave?.(); // Call the callback to refresh the parent component
      onClose();
    } catch (error: any) {
      console.error("Error updating login settings:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update login settings");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (originalPermissionData) {
      setClassicalProvider(originalPermissionData.classical_provider);
    }
    // Reset SSO providers to their original state
    fetchData();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Login Settings
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="mt-4 text-gray-600">
                Loading login settings...
              </span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Classical Provider */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Classical Authentication
                      </h3>
                      <p className="text-sm text-gray-600">
                        Traditional username/password login
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={classicalProvider}
                      onChange={(e) => setClassicalProvider(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {/* SSO Providers */}
              {ssoProviders.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-gray-900">
                      SSO Login Providers
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {ssoProviders.map((provider) => (
                      <div
                        key={provider._id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {provider.logo_url ? (
                              <img
                                src={provider.logo_url}
                                alt={provider.display_name}
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.nextElementSibling?.classList.remove(
                                    "hidden"
                                  );
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center ${
                                provider.logo_url ? "hidden" : ""
                              }`}
                            >
                              <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {provider.display_name}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {provider.provider_name} •{" "}
                                {provider.standard_type}
                              </p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={provider.enable}
                              onChange={() =>
                                handleSsoProviderToggle(provider._id)
                              }
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ssoProviders.length === 0 && (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Social Providers Found
                  </h3>
                  <p className="text-gray-600">
                    Configure SSO providers first to manage social login
                    settings.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Settings</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
